import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { toast } from 'react-toastify';
import api from '../../../api';

const MIN_REASON = 10;

const money = (amount, currency) => {
  const code = String(currency || 'gbp').toLowerCase();
  const symbol = code === 'gbp' ? '£' : `${code.toUpperCase()} `;
  return `${symbol}${Number(amount || 0).toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const firstError = (e, fallback) => {
  const errors = e.response?.data?.errors;
  return (errors && Object.values(errors).flat()[0]) || e.response?.data?.message || fallback;
};

/** Where this payment's refund stands, from the parent's side. */
function RefundStatus({ row }) {
  const request = row.request;
  if (row.refund_state === 'full') return <Chip size="small" label="Refunded" />;
  if (request?.status === 'pending') return <Chip size="small" color="warning" label="Refund requested" />;
  if (request?.status === 'approved') {
    return <Chip size="small" color="success" label={`Refunded ${money(request.refunded_amount, row.currency)}`} />;
  }
  if (row.refund_state === 'partial') {
    return <Chip size="small" color="success" label={`Refunded ${money(row.refunded_amount, row.currency)}`} />;
  }
  if (request?.status === 'rejected') return <Chip size="small" label="Not approved" />;
  if (request?.status === 'cancelled') return <Chip size="small" variant="outlined" label="Withdrawn" />;
  return <Chip size="small" color="success" variant="outlined" label="Paid" />;
}

function RequestDialog({ row, claim, onClose, onDone }) {
  const faulty = claim === 'faulty';
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    try {
      setSubmitting(true);
      const res = await api.post('parent/refund-requests', {
        purchasable_type: row.purchasable_type,
        purchase_id: row.purchase_id,
        reason: reason.trim(),
        claim,
      });
      toast.success(res.data?.message || 'Your refund request has been sent.');
      onDone();
    } catch (e) {
      toast.error(firstError(e, 'Could not send your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{faulty ? 'Report a problem' : 'Request a refund'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{row.product}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.kind, row.student_name, formatDate(row.purchased_at), money(row.amount, row.currency)]
                .filter((v) => v && v !== '—')
                .join(' · ')}
            </Typography>
          </Box>
          <TextField
            autoFocus
            required
            multiline
            minRows={4}
            label={faulty ? "What's wrong with it?" : 'Why would you like a refund?'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={
              reason.trim().length < MIN_REASON
                ? `Please give a short reason (at least ${MIN_REASON} characters).`
                : `${reason.length}/2000`
            }
          />
          <Alert severity="info">
            {faulty
              ? 'Tell us what is faulty or not as described. '
              : `You can ask for a refund within ${row.refund_policy?.window_days || 14} days of buying, as long as the item hasn't been started or downloaded. `}
            Our team reviews every request and emails you the outcome. If it is approved, the money goes back to the
            card or wallet you paid with (such as Google Pay or Apple Pay), usually within 5–10 business days.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || reason.trim().length < MIN_REASON}
          sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Send request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Parent panel → Billing → Refunds. The parent asks for a refund on one of their (or
 * their children's) card payments; an admin decides in Billing & Transactions →
 * Refund Requests, and the parent is emailed the outcome.
 */
export default function ParentRefundRequests() {
  const [rows, setRows] = useState(null);
  const [requesting, setRequesting] = useState(null); // { row, claim }
  const [withdrawingId, setWithdrawingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('parent/refund-requests');
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      toast.error(firstError(e, 'Failed to load your payments.'));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const withdraw = async (row) => {
    try {
      setWithdrawingId(row.request.id);
      await api.delete(`parent/refund-requests/${row.request.id}`);
      toast.success('Your refund request has been withdrawn.');
      load();
    } catch (e) {
      toast.error(firstError(e, 'Could not withdraw the request.'));
    } finally {
      setWithdrawingId(null);
    }
  };

  if (rows === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <CurrencyExchangeIcon sx={{ color: '#667eea' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Refunds</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Something not right with a purchase? Ask for a refund and our team will review it.
      </Typography>

      {rows.length === 0 ? (
        <Alert severity="info">No card payments on your account yet.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Refund</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.purchasable_type}-${row.purchase_id}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.purchased_at)}</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.product}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.kind}</Typography>
                    {row.request?.status === 'rejected' && (row.request.rejection_summary || row.request.admin_note) && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Our reply: {row.request.rejection_summary || row.request.admin_note}
                      </Typography>
                    )}
                    {row.request?.status === 'pending' && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Requested {formatDate(row.request.created_at)} — we will email you once it has been reviewed.
                      </Typography>
                    )}
                    {row.request?.status !== 'pending' && row.refund_policy?.message
                      && !['refunded', 'not_stripe'].includes(row.refund_policy.reason_code) && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, color: row.can_request_refund ? 'success.dark' : 'text.secondary' }}
                      >
                        {row.refund_policy.message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.student_name || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {money(row.amount, row.currency)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><RefundStatus row={row} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {row.request?.status === 'pending' && (
                      <Button
                        size="small"
                        onClick={() => withdraw(row)}
                        disabled={withdrawingId === row.request.id}
                        sx={{ textTransform: 'none' }}
                      >
                        Withdraw
                      </Button>
                    )}
                    {row.can_request_refund && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setRequesting({ row, claim: 'change_of_mind' })}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#667eea', color: '#667eea' }}
                      >
                        {row.request?.status === 'rejected' ? 'Ask again' : 'Request refund'}
                      </Button>
                    )}
                    {row.can_report_problem && (
                      <Button
                        size="small"
                        onClick={() => setRequesting({ row, claim: 'faulty' })}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#667eea' }}
                      >
                        Report a problem
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {requesting && (
        <RequestDialog
          row={requesting.row}
          claim={requesting.claim}
          onClose={() => setRequesting(null)}
          onDone={() => { setRequesting(null); load(); }}
        />
      )}
    </Box>
  );
}
